import { useCallback, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { useDispatch, useSelector } from "react-redux";

import { GeoJSONSource, Map } from "maplibre-gl";
import type { Feature, FeatureCollection } from "geojson";

import { useGetSettings } from "@/api/configuration/useConfigurationAPI";
import PolygonPopup from "@/components/map/PolygonPopup";
import {
  DRAW_POPUP_INFO_LAYER,
  DRAW_POPUP_INFO_SOURCE,
  MAX_AREA
} from "@/constants/map";
import { RootState, store } from "@/store";
import {
  addPolygonSettingsTemp,
  editGeometryPolygonSettingsTemp,
  removePolygonItemSettingsTemp,
  setDrawPolygonDataset,
  updatePolygonSettingsTemp
} from "@/store/slice/spatialSlice";
import { setShowDrawLimitInfo } from "@/store/slice/uiSlice";
import { AreaInputType } from "@/types/spatial";
import { truncateGeoJson } from "@/utils/map";

import MapboxDraw from "@mapbox/mapbox-gl-draw";
import {
  area,
  centerOfMass,
  length as turfLength,
  lineString
} from "@turf/turf";

import { useDrawStyles } from "./useDrawStyles";

const formatKm = (km: number) => `${km.toFixed(2)} km`;
const kmToMiles = (km: number) => km * 0.621371;
const formatMiles = (km: number) => {
  const mi = kmToMiles(km);
  return `${mi.toFixed(2)} mi`;
};

const DRAW_CONFIG = {
  displayControlsDefault: false,
  boxSelect: false,
  controls: {
    polygon: true,
    line_string: false,
    point: false,
    trash: false,
    combine_features: false,
    uncombine_features: false
  }
} as const;

const POPUP_CONFIG = {
  maxWidth: "352px"
} as const;

const POLYGON_NAME_PREFIX = "Location layer polygon #";

interface UseMapboxDrawReturn {
  enterDrawMode: () => void;
  exitDrawMode: () => void;
  enterEditMode: (featureId: string) => void;
  enterSelectMode: () => void;
  renderDrawPolygon: () => void;
  getAllFeatures: () => FeatureCollection;
  deleteAllFeatures: () => void;
  synchronizeFeatures: () => void;
  addFeature: (feature: Feature) => string | string[] | undefined;
  deleteFeature: (featureId: string) => void;
}

const StaticMode: MapboxDraw.DrawCustomMode = {
  onSetup: function () {
    this.clearSelectedFeatures();
    this.setActionableState({
      trash: false,
      combineFeatures: false,
      uncombineFeatures: false
    });
    return {};
  },
  toDisplayFeatures: function (_, geojson, display) {
    display(geojson);
  }
};

export const useMapboxDraw = (
  map: Map | null,
  popup: maplibregl.Popup | null
): UseMapboxDrawReturn => {
  const dispatch = useDispatch();
  const {
    isDrawPolygon,
    showAnalysisResultPopup,
    showAnalysisSummaryPopup,
    isCustomDrawPolygonDataset
  } = useSelector((state: RootState) => state.ui);

  const { data } = useGetSettings();

  const {
    polygonSettingsTemp,
    polygonSettings,
    spatialConfig,
    currRunningAnalysisId
  } = useSelector((state: RootState) => state.spatial);

  const drawRef = useRef<MapboxDraw | null>(null);
  const styles = useDrawStyles();

  const cursorTooltipRef = useRef<HTMLDivElement | null>(null);
  const lastCursorRef = useRef<{ x: number; y: number } | null>(null);

  const bringDrawLayersToFront = useCallback(() => {
    if (!map) return;

    const style = map.getStyle();
    if (!style?.layers) return;

    // Mapbox Draw creates layers with .cold and .hot suffixes
    // We need to find all layers starting with 'gl-draw' and move them to front
    const drawLayerIds = style.layers
      .filter((layer) => layer.id.startsWith("gl-draw"))
      .map((layer) => layer.id);

    // Move each draw layer to the front
    drawLayerIds.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.moveLayer(layerId);
      }
    });

    // Move popup info layer to front as well
    if (map.getLayer(DRAW_POPUP_INFO_LAYER)) {
      map.moveLayer(DRAW_POPUP_INFO_LAYER);
    }
  }, [map]);

  useEffect(() => {
    if (!map) return;

    const draw = new MapboxDraw({
      ...DRAW_CONFIG,
      modes: { ...MapboxDraw.modes, static: StaticMode },
      styles
    });

    drawRef.current = draw;
    map.addControl(drawRef.current as unknown as maplibregl.IControl);

    map.on("draw.create", onCreated);
    map.on("draw.update", onUpdated);
    map.on("draw.selectionchange", onSelectionChange);
    // Force to enter draw polygon mode if it's a custom draw polygon dataset
    if (
      isCustomDrawPolygonDataset &&
      drawRef.current.getMode() !== "draw_polygon"
    ) {
      enterDrawMode();
    }

    return () => {
      map.off("draw.create", onCreated);
      map.off("draw.update", onUpdated);
      map.off("draw.selectionchange", onSelectionChange);
      if (map.hasControl(drawRef.current as unknown as maplibregl.IControl)) {
        map.removeControl(drawRef.current as unknown as maplibregl.IControl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, styles, data?.max_catchments]);

  useEffect(() => {
    if (
      map &&
      drawRef.current &&
      spatialConfig.areaInput?.value === AreaInputType.POLYGON &&
      !currRunningAnalysisId &&
      !showAnalysisResultPopup &&
      !showAnalysisSummaryPopup
    ) {
      map.on("load", () => {
        renderDrawPolygon();
      });
    }

    if (!isDrawPolygon) {
      drawRef.current?.changeMode("static");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, polygonSettingsTemp, currRunningAnalysisId]);

  const getAllFeatures = useCallback((): FeatureCollection => {
    if (drawRef.current) {
      return drawRef.current.getAll();
    }
    return { type: "FeatureCollection", features: [] };
  }, []);

  const deleteAllFeatures = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.deleteAll();
    }
  }, []);

  const addFeature = useCallback(
    (feature: Feature): string | string[] | undefined => {
      if (drawRef.current) {
        return drawRef.current.add(feature);
      }
      return undefined;
    },
    []
  );

  const showCursorTooltip = (
    html: string,
    screenX: number,
    screenY: number
  ) => {
    const el = cursorTooltipRef.current;
    if (!el) return;
    el.innerHTML = html;
    const offset = 6;
    el.style.left = `${screenX + offset}px`;
    el.style.top = `${screenY + offset}px`;
    el.style.display = "block";
  };

  const createTooltipCursor = useCallback(() => {
    if (!map) return null;
    if (!cursorTooltipRef.current) {
      const el = document.createElement("div");
      el.className =
        "pointer-events-none bg-white rounded-lg p-1 shadow-md border border-gray-200 text-[10px]";
      el.style.position = "absolute";
      el.style.zIndex = "10";
      el.style.display = "none";
      el.title = "cursor-tooltip";
      el.dataset.testid = "cursor-tooltip";
      map.getContainer().appendChild(el);
      cursorTooltipRef.current = el;
    }
    return cursorTooltipRef.current;
  }, [map]);

  const hideCursorTooltip = () => {
    const el = cursorTooltipRef.current;
    if (!el) return;
    el.style.display = "none";
  };

  const createPopupInfoLayer = useCallback(() => {
    if (!map) return;
    const style = map.getStyle();

    if (!style?.layers) return;
    if (!map.getSource(DRAW_POPUP_INFO_SOURCE)) {
      map.addSource(DRAW_POPUP_INFO_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] }
      });
    }
    if (!map.getLayer(DRAW_POPUP_INFO_LAYER)) {
      map.addLayer({
        id: DRAW_POPUP_INFO_LAYER,
        type: "symbol",
        source: DRAW_POPUP_INFO_SOURCE,
        layout: {},
        paint: {}
      });
    }
  }, [map]);

  useEffect(() => {
    if (!map || !drawRef.current) return;

    const ensureModeIsDrawPolygon = () => {
      const mode = drawRef.current?.getMode?.() ?? "";
      return mode === "draw_polygon";
    };

    const refreshTooltip = (pt?: { x: number; y: number }) => {
      if (!ensureModeIsDrawPolygon()) {
        hideCursorTooltip();
        return;
      }

      const point = pt ?? lastCursorRef.current;
      if (!point) {
        hideCursorTooltip();
        return;
      }

      const all = drawRef.current!.getAll();
      const poly = [...all.features]
        .reverse()
        .find((f) => f.geometry?.type === "Polygon");
      const km = getLastFixedSegmentKm(poly);

      if (km == null) {
        hideCursorTooltip();
        return;
      }

      const html = `
      <div class="flex flex-col">
        <span><b>${formatKm(km)}</b></span>
        <span><b>${formatMiles(km)}</b></span>
      </div>
    `;
      showCursorTooltip(html, point.x, point.y);
    };

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      const p = e.point as { x: number; y: number } | undefined;
      if (p) lastCursorRef.current = { x: p.x, y: p.y };
      refreshTooltip(p);
    };

    const onClick = () => refreshTooltip();

    const onDrawRender = () => {
      refreshTooltip();
      bringDrawLayersToFront();
    };

    map.on("mousemove", onMouseMove);
    map.on("click", onClick);
    map.on("draw.render", onDrawRender);

    // Listen for style changes (e.g., when new dataset layers are added)
    // to ensure draw layers stay on top
    const onStyleData = () => {
      bringDrawLayersToFront();
    };
    map.on("styledata", onStyleData);

    return () => {
      map.off("mousemove", onMouseMove);
      map.off("click", onClick);
      map.off("draw.render", onDrawRender);
      map.off("styledata", onStyleData);
      hideCursorTooltip();
      lastCursorRef.current = null;
    };
  }, [map, createTooltipCursor, bringDrawLayersToFront]);

  const getLastFixedSegmentKm = (poly: Feature | undefined) => {
    if (!poly || poly.geometry?.type !== "Polygon") return null;
    const rings = poly.geometry.coordinates;

    const ring = rings?.[0] ?? [];
    const len = ring.length;
    if (len <= 2) return null;

    const aIdx = len - 3;
    const bIdx = len - 2;

    const a = ring[aIdx];
    const b = ring[bIdx];

    if (!a || !b) return null;

    const km = turfLength(lineString([a, b]), { units: "kilometers" });
    return km;
  };

  const deleteFeature = useCallback(
    (featureId: string) => {
      const isPolygonTempExist =
        store.getState().spatial.polygonSettingsTemp &&
        store.getState().spatial.polygonSettingsTemp[featureId];

      if (drawRef.current) {
        const feature = drawRef.current.get(featureId);
        if (feature) {
          if (isPolygonTempExist) {
            dispatch(removePolygonItemSettingsTemp(featureId));
          }

          drawRef.current.delete(featureId);
        } else {
          console.warn(`Feature with ID ${featureId} not found.`);
        }
      } else {
        console.warn("Draw instance not available.");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [polygonSettingsTemp]
  );

  const synchronizeFeatures = useCallback(() => {
    deleteAllFeatures();

    Object.entries(polygonSettings).forEach(([_, polygonData]) => {
      if (polygonData?.polygon) {
        addFeature(polygonData.polygon as Feature);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polygonSettings]);

  const showPopup = useCallback(
    (feature: Feature) => {
      if (feature && feature.geometry.type === "Polygon") {
        const center = centerOfMass(feature);
        const centerCoords = center.geometry.coordinates as [number, number];
        const areaCalculate = area(feature) / 1000000;

        const tempDiv = document.createElement("div");
        const root = createRoot(tempDiv);

        // Store root reference for cleanup
        const rootRef = { current: root };

        root.render(
          <PolygonPopup
            dispatch={dispatch}
            updatePolygonSettingsTemp={updatePolygonSettingsTemp}
            areaCalculate={areaCalculate}
            feature={feature}
            popup={popup}
            rootRef={rootRef}
            draw={drawRef.current}
            deleteFeature={deleteFeature}
            map={map}
            isCustomDrawPolygonDataset={isCustomDrawPolygonDataset}
            setDrawPolygonDataset={setDrawPolygonDataset}
          />
        );

        // Show the popup with the React content
        if (popup) {
          popup
            .setLngLat(centerCoords)
            .setMaxWidth(POPUP_CONFIG.maxWidth)
            .setDOMContent(tempDiv)
            .addTo(map as Map);
        }
      }
    },
    [dispatch, popup, deleteFeature, map, isCustomDrawPolygonDataset]
  );

  const enterDrawMode = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.changeMode("draw_polygon");
      createPopupInfoLayer();

      const src = map?.getSource<InstanceType<typeof GeoJSONSource>>(
        DRAW_POPUP_INFO_SOURCE
      );
      src?.setData({ type: "FeatureCollection", features: [] });
      createTooltipCursor();
      hideCursorTooltip();
      lastCursorRef.current = null;

      // Ensure layers are on top when entering mode
      bringDrawLayersToFront();
    }
  }, [map, createPopupInfoLayer, createTooltipCursor, bringDrawLayersToFront]);

  const enterEditMode = useCallback((featureId: string) => {
    if (drawRef.current) {
      drawRef.current.changeMode("direct_select", { featureId });
    }
  }, []);

  const enterSelectMode = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.changeMode("simple_select");
    }
  }, []);

  const exitDrawMode = useCallback(() => {
    if (drawRef.current) {
      popup?.remove();
      drawRef.current.changeMode("static");

      hideCursorTooltip();
      const src = map?.getSource<InstanceType<typeof GeoJSONSource>>(
        DRAW_POPUP_INFO_SOURCE
      );
      src?.setData({ type: "FeatureCollection", features: [] });
      lastCursorRef.current = null;
    }
  }, [popup, map]);

  const getNextPolygonSequence = useCallback(() => {
    const {
      polygonSettingsTemp: tempSettings,
      polygonSettings: savedSettings
    } = store.getState().spatial;

    const parseSequence = (value: unknown): number => {
      if (typeof value === "number" && !Number.isNaN(value)) {
        return value;
      }

      if (typeof value === "string") {
        const withoutPrefix = value.startsWith(POLYGON_NAME_PREFIX)
          ? value.slice(POLYGON_NAME_PREFIX.length)
          : value;

        const numericPortion = withoutPrefix.replace(/[^0-9]/g, "");
        if (!numericPortion) return 0;

        const parsed = parseInt(numericPortion, 10);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }

      return 0;
    };

    const extractMaxSequence = (
      items:
        | Record<string, { name?: string; sequence?: number | string }>
        | Feature[]
    ): number =>
      Object.values(items).reduce((max, item) => {
        const props = "properties" in item ? item.properties : item;
        if (!props) return max;
        const { sequence, name } = props;
        return Math.max(max, parseSequence(sequence ?? name));
      }, 0);

    const maxExistingSequence = Math.max(
      extractMaxSequence(tempSettings ?? {}),
      extractMaxSequence(savedSettings ?? {}),
      extractMaxSequence(getAllFeatures().features)
    );

    return maxExistingSequence + 1;
  }, [getAllFeatures]);

  const onCreated = useCallback(
    (e: MapboxDraw.DrawCreateEvent) => {
      const polygonSettingsTemp = store.getState().spatial.polygonSettingsTemp;
      const polygonSettings = store.getState().spatial.polygonSettings;

      // Checking polygonSettingsTemp when draw polygon panel shows and polygonSettings when draw polygon panel is hidden
      if (
        Object.values(polygonSettingsTemp).length >=
          (data?.max_catchments || 100) ||
        Object.values(polygonSettings).length >= (data?.max_catchments || 100)
      ) {
        dispatch(setShowDrawLimitInfo(true));

        const feature = e.features[0];
        drawRef.current?.delete(feature.id as string);
        return;
      }

      const feature = e.features[0];
      const truncatedPolygon = truncateGeoJson(feature);
      const sequence = getNextPolygonSequence();
      const polygonName = `${POLYGON_NAME_PREFIX}${sequence}`;
      const truncatedPolygonForPopup = {
        ...truncatedPolygon,
        properties: {
          ...(truncatedPolygon.properties ?? {}),
          name: polygonName,
          sequence
        }
      } as Feature;

      drawRef.current?.setFeatureProperty(
        truncatedPolygon.id as string,
        "name",
        polygonName
      );
      drawRef.current?.setFeatureProperty(
        truncatedPolygon.id as string,
        "sequence",
        sequence
      );

      showPopup(truncatedPolygonForPopup);

      if (
        area(feature) / 1000000 <
          (Number(import.meta.env.VITE_MAX_DRAW_AREA_SIZE) || MAX_AREA) &&
        !isCustomDrawPolygonDataset
      ) {
        dispatch(
          addPolygonSettingsTemp({
            id: truncatedPolygon.id as string,
            name: polygonName,
            sequence,
            polygon: truncatedPolygon,
            areaSize: area(truncatedPolygon) / 1000000
          })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.max_catchments, getNextPolygonSequence]
  );

  const onUpdated = useCallback(
    (e: MapboxDraw.DrawUpdateEvent) => {
      const feature = e.features[0];
      const truncatedPolygon = truncateGeoJson(feature);

      dispatch(
        editGeometryPolygonSettingsTemp({
          id: truncatedPolygon.id as string,
          polygon: truncatedPolygon.geometry
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showPopup]
  );

  const onSelectionChange = useCallback(
    (e: MapboxDraw.DrawSelectionChangeEvent) => {
      const feature = e.features[0];
      const truncatedPolygon = truncateGeoJson(feature);

      showPopup(truncatedPolygon);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [popup, isDrawPolygon]
  );

  const renderDrawPolygon = useCallback(() => {
    if (!map) return;
    const style = map.getStyle();

    if (!style?.layers) return;

    style.layers.forEach((layer) => {
      if (layer.id.startsWith("gl-draw")) {
        map.setLayoutProperty(layer.id, "visibility", "visible");
      }
    });

    bringDrawLayersToFront();

    if (Object.keys(polygonSettings).length > 0 && map) {
      Object.values(polygonSettings).forEach((value) => {
        addFeature(value.polygon as Feature);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polygonSettings, map]);

  return {
    enterDrawMode,
    exitDrawMode,
    enterEditMode,
    enterSelectMode,
    renderDrawPolygon,
    getAllFeatures,
    deleteAllFeatures,
    synchronizeFeatures,
    addFeature,
    deleteFeature
  };
};
